import { safeBlogHtml } from "../../utils/safeBlogHtml";
import { displayLabel } from "../../utils/displayLabel";
// AuthorBlog.js — same-page post switching with deep-linkable URL (?p=slug)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen } from "lucide-react";
import ReaderPageLayout from "../layouts/ReaderPageLayout";

const BLOG_API =
  "https://y32k94fh1c.execute-api.us-west-2.amazonaws.com/default/BlogPostHandler";
const FETCH_MODE = "host"; // "host" | "folder"

export default function AuthorBlog({ posts: suppliedPosts, title = "Author Blog", groupBlog = false }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const topRef = useRef(null);

  const host = useMemo(
    () => (typeof window !== "undefined" ? window.location.host : ""),
    []
  );

  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});

  const slugify = (s = "") =>
    String(s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const fmt = (iso) => {
    if (!iso || iso === "undated") return "Date unavailable";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso || "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      setError("We could not load this blog. Please retry.");
      setLoading(false);
    }, 15000);
    (async () => {
      try {
        setLoading(true);
        setError("");

        const qs =
          FETCH_MODE === "host"
            ? `?host=${encodeURIComponent(host)}`
            : `?folder=${encodeURIComponent("PUBLIC_FOLDER_PATH_ON_SERVER")}`;

        const res = suppliedPosts ? { ok: true, json: async () => suppliedPosts } : await fetch(`${BLOG_API}${qs}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Blog API ${res.status}`);
        const data = await res.json();

        if (controller.signal.aborted) return;
        if (!Array.isArray(data) && !Array.isArray(data?.posts)) throw new Error("Incomplete blog response");
        const raw = Array.isArray(data)
          ? data
          : Array.isArray(data?.posts)
          ? data.posts
          : [];
        const pub = raw.filter(
          (p) => p && typeof p === "object" && !Array.isArray(p) && (p.isPublished === true || (p.isPublished === undefined && p.public === true))
        );

        const norm = pub
          .map((p, index) => ({
            id: String([p.id, p.postId, p.createdAt].find(value => (typeof value === "string" && value) || (typeof value === "number" && Number.isFinite(value))) ?? `legacy-${index}`),
            title: typeof p.title === "string" ? p.title : "Untitled Post",
            content: typeof p.content === "string" ? p.content : "",
            createdAt: Number.isFinite(Date.parse(p.createdAt || p.date)) ? new Date(p.createdAt || p.date).toISOString() : null,
            postUrl: null,
            slug: typeof p.slug === "string" && p.slug ? p.slug : slugify(typeof p.title === "string" ? p.title : ""),
          }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setPosts(norm);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError("We could not load this blog. Please retry.");
      } finally {
        clearTimeout(timeout);
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => { clearTimeout(timeout); controller.abort(); };
  }, [host, suppliedPosts, retry]);

  useEffect(() => {
    if (posts.length === 0) { setSelected(null); return; }

    const params = new URLSearchParams(search);
    const qSlug = params.get("p");

    let pathSlug = null;
    const pathMatch = pathname.match(/\/blog\/([^/?#]+)/i);
    if (pathMatch) { try { pathSlug = decodeURIComponent(pathMatch[1]); } catch { pathSlug = pathMatch[1]; } }

    const wantSlug = qSlug || pathSlug;
    const bySlug = wantSlug && (posts.find((p) => p.id === wantSlug) || posts.find((p) => p.slug === wantSlug));

    setSelected(wantSlug ? bySlug || null : posts[0]);
  }, [posts, pathname, search]);

  const archive = useMemo(() => {
    return posts.reduce((acc, p) => {
      const d = p.createdAt ? new Date(p.createdAt) : null;
      const y = d ? d.getFullYear() : "Undated";
      const m = d ? d.toLocaleString(undefined, { month: "long" }) : "Date unavailable";
      const dayKey = d ? d.toISOString().slice(0, 10) : "undated";

      acc[y] ??= {};
      acc[y][m] ??= {};
      acc[y][m][dayKey] ??= [];
      acc[y][m][dayKey].push(p);
      return acc;
    }, {});
  }, [posts]);

  const toggleYear = (y) =>
    setExpandedYears((prev) => ({ ...prev, [y]: !prev[y] }));
  const toggleMonth = (y, m) =>
    setExpandedMonths((prev) => ({
      ...prev,
      [`${y}-${m}`]: !prev[`${y}-${m}`],
    }));

  const openPost = (p) => {
    if (p.postUrl) {
      try {
        const u = new URL(p.postUrl);
        if (u.host && u.host !== host) {
          window.open(p.postUrl, "_blank", "noopener,noreferrer");
          return;
        }
      } catch {
        // noop
      }
    }
    setSelected(p);
    const next = new URLSearchParams(search);
    next.set("p", p.id);
    navigate({ pathname: pathname.replace(/\/blog\/[^/]+\/?$/, "/blog"), search: `?${next.toString()}` }, { replace: false });
    topRef.current?.scrollIntoView?.({ behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  };

  return (
    <div className={groupBlog ? "bg-bookish-cream text-bookish-royal min-h-screen" : undefined}><ReaderPageLayout
      title={title}
      description={groupBlog ? "Notes, stories, and new chapters from our publishing group." : "Read the latest posts and browse the archive."}
      contentClassName="space-y-6"
    >
      {groupBlog && <div className="flex flex-wrap items-center gap-4"><BookOpen size={30} aria-hidden="true" className="text-bookish-purple" /><p className="font-serif text-xl">A little reading between books.</p><a href="/authors" className="underline min-h-[44px] inline-flex items-center">Meet our authors</a></div>}
      {loading && (
        <div role="status" aria-label="Loading blog posts" className="bg-white rounded-2xl shadow-md p-6 space-y-4 animate-pulse"><span className="sr-only">Opening the latest chapter…</span>
          <div className="h-8 bg-gray-200 rounded w-2/3" />
          <div className="h-4 bg-gray-200 rounded w-1/4" />
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-11/12" />
          <div className="h-4 bg-gray-200 rounded w-10/12" />
        </div>
      )}

      {error && !loading && (
        <div className="bg-white rounded-2xl shadow-md p-6 text-center text-red-600">
          <p role="alert">{error}</p><button type="button" className="underline min-h-[44px]" onClick={() => setRetry(value => value + 1)}>Retry loading</button>
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="bg-white rounded-2xl shadow-md p-6 text-sm sm:text-base text-gray-700">
          {groupBlog ? "Our first chapter is still being written. Please check back for new posts." : "No blog posts yet."}
        </div>
      )}

      {!loading && !error && posts.length > 0 && !selected && <div className="bg-white rounded-2xl p-6"><p>This post is no longer available.</p><button className="underline min-h-[44px]" onClick={() => { const next = new URLSearchParams(search); next.delete("p"); navigate({ pathname: pathname.replace(/\/blog\/[^/]+\/?$/, "/blog"), search: next.toString() }); }}>Browse current posts</button></div>}
      {!loading && !error && posts.length > 0 && selected && (
        <>
          <nav aria-label="Recent posts" className="flex flex-wrap gap-3">{posts.slice(0, 5).map(post => <button key={post.id} type="button" aria-current={selected.id === post.id ? 'page' : undefined} onClick={() => openPost(post)} className="rounded-full border border-bookish-purple/25 bg-white text-bookish-purple px-4 py-3 text-left break-words">{displayLabel(post.title)}</button>)}</nav>
          <article ref={topRef} className="bg-white rounded-2xl shadow-sm border border-bookish-purple/20 p-6 sm:p-8 space-y-4 break-words">
            <h2 className="text-2xl sm:text-3xl font-serif text-bookish-purple">
              {displayLabel(selected.title)}
            </h2>
            <p className="text-sm sm:text-base text-gray-700">{fmt(selected.createdAt)}</p>

            <div
              className="prose prose-sm sm:prose-base max-w-none text-gray-700 [&_p]:clear-both"
              dangerouslySetInnerHTML={{ __html: safeBlogHtml(selected.content) }}
            />
          </article>

          <section className="bg-white rounded-2xl shadow-md p-6 space-y-4">
            <h3 className="text-2xl sm:text-3xl font-serif text-bookish-purple">Archive</h3>
            <div className="space-y-4">
              {Object.keys(archive)
                .sort((a, b) => Number(b) - Number(a))
                .map((year) => (
                  <div key={year} className="rounded-2xl border border-bookish-purple/15">
                    <button
                      aria-expanded={!!expandedYears[year]}
                      onClick={() => toggleYear(year)}
                      className="w-full text-left px-5 py-3 text-sm sm:text-base font-semibold text-bookish-purple flex items-center justify-between"
                    >
                      <span>{year}</span>
                      <span>{expandedYears[year] ? "▾" : "▸"}</span>
                    </button>

                    {expandedYears[year] &&
                      Object.keys(archive[year])
                        .sort((a, b) => {
                          const A = new Date(`${a} 1, ${year}`).getTime();
                          const B = new Date(`${b} 1, ${year}`).getTime();
                          return B - A;
                        })
                        .map((month) => (
                          <div key={month} className="border-t border-bookish-purple/10">
                            <button
                              aria-expanded={!!expandedMonths[`${year}-${month}`]}
                              onClick={() => toggleMonth(year, month)}
                              className="w-full text-left px-6 py-2 text-sm sm:text-base text-gray-700 flex items-center justify-between"
                            >
                              <span>{month}</span>
                              <span>{expandedMonths[`${year}-${month}`] ? "▾" : "▸"}</span>
                            </button>

                            {expandedMonths[`${year}-${month}`] && (
                              <div className="px-6 pb-4">
                                {Object.keys(archive[year][month])
                                  .sort((a, b) => new Date(b) - new Date(a))
                                  .map((dayKey) => (
                                    <div key={dayKey} className="mb-2">
                                      <div className="text-xs text-gray-700 mb-1">{fmt(dayKey)}</div>
                                      <ul className="space-y-1">
                                        {archive[year][month][dayKey].map((p) => (
                                          <li key={p.id} className="pl-2">
                                            <button
                                              type="button"
                                              onClick={() => openPost(p)}
                                              aria-current={selected.id === p.id ? "page" : undefined}
                                              className="rounded-xl px-4 py-2 text-sm font-semibold bg-bookish-purple text-white hover:bg-bookish-purple/90 transition"
                                            >
                                              {displayLabel(p.title)}
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                  </div>
                ))}
            </div>
          </section>
        </>
      )}
    </ReaderPageLayout></div>
  );
}
